import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, QrCode, Newspaper, ArrowRight, AlertCircle, Clock, Building2, Wallet, AlertTriangle, Calendar, MapPin } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';

export default function MemberDashboard() {
  const { user } = useAuth();
  const [credential, setCredential] = useState(null);
  const [posts, setPosts] = useState([]);
  const [billing, setBilling] = useState(null);
  const [propertyCount, setPropertyCount] = useState(0);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const isMember = user?.role === 'member' || user?.role === 'admin' || user?.role === 'superadmin';

  useEffect(() => {
    api.get('/news').then(({ data }) => setNews((data || []).slice(0, 3))).catch(() => {});
    api.get('/events').then(({ data }) => setEvents((data || []).slice(0, 3))).catch(() => {});
    if (isMember) {
      api.get('/me/credential').then(({ data }) => setCredential(data)).catch(() => {});
      api.get('/community/posts').then(({ data }) => setPosts(data.slice(0, 3))).catch(() => {});
      api.get('/me/invoices').then(({ data }) => setBilling(data)).catch(() => {});
      api.get('/properties/mine').then(({ data }) => setPropertyCount(data.length)).catch(() => {});
    }
  }, [isMember]);

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="member-dashboard">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Panel del socio</p>
        <h1 className="mt-1 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">
          Hola, {user?.name?.split(' ')[0]}
        </h1>
      </div>

      {!isMember && (
        <div className="flex items-start gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-6" data-testid="membership-pending-banner">
          <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-amber-500" />
          <div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Tu cuenta aún no tiene membresía activa</h3>
            <p className="mt-1 text-sm text-slate-600">
              Si ya enviaste tu solicitud, está en revisión por el equipo CNIB. Si aún no lo hiciste, completa tu
              afiliación para desbloquear la credencial, el feed y todos los módulos.
            </p>
            <Link
              to="/hagase-socio"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-orange-600"
              data-testid="dashboard-hagase-socio-button"
            >
              Completar afiliación <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}

      {isMember && billing?.billing_status === 'overdue' && (
        <div className="flex items-start gap-4 rounded-2xl border border-red-200 bg-red-50 p-6" data-testid="billing-overdue-banner">
          <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-red-500" />
          <div>
            <h3 className="font-heading text-lg font-bold text-red-700">Tienes cuotas en mora</h3>
            <p className="mt-1 text-sm text-red-600">
              Regulariza tu pago para mantener activa tu credencial y la publicación de propiedades.
            </p>
            <Link
              to="/app/cuotas"
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-red-500 px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-red-600"
              data-testid="dashboard-cuotas-link"
            >
              Ver mis cuotas <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}

      {isMember && (
        <>
          <div className="grid gap-5 sm:grid-cols-3">
            <div className="hero-grain relative overflow-hidden rounded-2xl bg-cnib-navy p-6 sm:col-span-2">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Estado de membresía</p>
                  <p className="mt-2 flex items-center gap-2 font-heading text-2xl font-extrabold text-white">
                    <BadgeCheck className="h-6 w-6 text-emerald-400" />
                    {credential?.status === 'active' ? 'Socio vigente' : 'Vigencia por verificar'}
                  </p>
                  {credential?.valid_until && (
                    <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-300">
                      <Clock className="h-4 w-4 text-orange-500" />
                      Vigente hasta el{' '}
                      {new Date(credential.valid_until).toLocaleDateString('es-BO', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </p>
                  )}
                  <p className="mt-1 font-mono text-sm text-orange-400">{credential?.member_number}</p>
                </div>
                <Link
                  to="/app/credencial"
                  data-testid="dashboard-credential-link"
                  className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500 text-white transition-transform hover:scale-105"
                >
                  <QrCode className="h-5 w-5" />
                </Link>
              </div>
            </div>
            <Link
              to="/app/feed"
              data-testid="dashboard-feed-link"
              className="group flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cnib-blue/10">
                <Newspaper className="h-5 w-5 text-cnib-blue" />
              </div>
              <div className="mt-4">
                <p className="font-heading font-bold text-slate-900">Feed profesional</p>
                <p className="mt-1 text-sm text-slate-500">Comparte oportunidades con la red</p>
              </div>
              <ArrowRight className="mt-3 h-4 w-4 text-slate-300 transition-all group-hover:translate-x-1 group-hover:text-orange-500" />
            </Link>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <Link
              to="/app/propiedades"
              data-testid="dashboard-properties-link"
              className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500/10">
                  <Building2 className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="font-heading font-bold text-slate-900">Mis propiedades</p>
                  <p className="text-sm text-slate-500">{propertyCount} publicada(s) en la red</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300 transition-all group-hover:translate-x-1 group-hover:text-orange-500" />
            </Link>
            <Link
              to="/app/cuotas"
              data-testid="dashboard-dues-link"
              className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className="flex items-center gap-4">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${billing?.billing_status === 'overdue' ? 'bg-red-500/10' : 'bg-emerald-500/10'}`}>
                  <Wallet className={`h-5 w-5 ${billing?.billing_status === 'overdue' ? 'text-red-500' : 'text-emerald-500'}`} />
                </div>
                <div>
                  <p className="font-heading font-bold text-slate-900">Mis cuotas</p>
                  <p className="text-sm text-slate-500">
                    {billing ? (billing.billing_status === 'overdue' ? 'Tienes cuotas en mora' : 'Membresía al día') : 'Ver estado de membresía'}
                  </p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300 transition-all group-hover:translate-x-1 group-hover:text-orange-500" />
            </Link>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="dashboard-latest-news">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-slate-900">Últimas noticias</h2>
                <Link to="/noticias" className="text-sm font-bold text-orange-500 hover:text-orange-600" data-testid="dashboard-news-view-all">
                  Ver más <ArrowRight className="inline h-3 w-3" />
                </Link>
              </div>
              <div className="mt-3 space-y-3">
                {news.length === 0 && <p className="text-sm text-slate-500">Aún no hay noticias publicadas.</p>}
                {news.map((n) => (
                  <Link key={n.slug || n.article_id} to={`/noticias/${n.slug}`} data-testid={`dashboard-news-${n.slug || n.article_id}`} className="flex gap-3 overflow-hidden rounded-xl bg-slate-50 p-2 transition-colors hover:bg-slate-100">
                    {n.image_url && <img src={n.image_url} alt="" className="h-16 w-20 shrink-0 rounded-lg object-cover" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-orange-500">{n.category || 'Noticia'}</p>
                      <p className="line-clamp-2 text-sm font-bold text-slate-900">{n.title}</p>
                      <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400">
                        <Clock className="h-3 w-3" /> {n.reading_minutes || 2} min
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="dashboard-upcoming-events">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-slate-900">Próximos eventos</h2>
                <Link to="/eventos" className="text-sm font-bold text-orange-500 hover:text-orange-600" data-testid="dashboard-events-view-all">
                  Ver más <ArrowRight className="inline h-3 w-3" />
                </Link>
              </div>
              <div className="mt-3 space-y-3">
                {events.length === 0 && <p className="text-sm text-slate-500">Aún no hay eventos programados.</p>}
                {events.map((e) => (
                  <Link key={e.slug} to={`/eventos/${e.slug}`} data-testid={`dashboard-event-${e.slug}`} className="flex gap-3 overflow-hidden rounded-xl bg-slate-50 p-2 transition-colors hover:bg-slate-100">
                    {e.cover_image && <img src={e.cover_image} alt="" className="h-16 w-20 shrink-0 rounded-lg object-cover" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-cnib-blue">EVENTO</p>
                      <p className="line-clamp-2 text-sm font-bold text-slate-900">{e.title}</p>
                      <p className="mt-1 flex items-center gap-2 text-[11px] text-slate-400">
                        <Calendar className="h-3 w-3" /> {String(e.starts_at || '').slice(0, 10)}
                        <MapPin className="h-3 w-3" /> {e.city}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-slate-900">Actividad reciente de la comunidad</h2>
              <Link to="/app/feed" className="text-sm font-bold text-orange-500 hover:text-orange-600">
                Ver todo
              </Link>
            </div>
            <div className="mt-4 space-y-4">
              {posts.length === 0 && <p className="text-sm text-slate-500">Aún no hay publicaciones.</p>}
              {posts.map((post) => (
                <div key={post.post_id} className="rounded-xl bg-slate-50 p-4">
                  <p className="text-sm font-bold text-slate-800">{post.author_name}</p>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-600">{post.text}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
